import { isFormulaHandlePoint, type NumberBinding, type ObjectDefinitionSpec } from 'shared-types';

type SmartSpecFields = Pick<ObjectDefinitionSpec, 'params' | 'formulas' | 'stepSyncs' | 'depthLink' | 'handles' | 'groups' | 'shapes'>;

const readsName = (expression: string | undefined, names: readonly string[]): boolean => expression !== undefined && names.some((name) => new RegExp(`\\b${name}\\b`).test(expression));

const bindingReadsName = (binding: NumberBinding, names: readonly string[]): boolean => ('param' in binding ? names.includes(binding.param) : 'expr' in binding && readsName(binding.expr, names));

/**
 * Whether a built-in drawing counts as a smart object: a parametric one,
 * with rules and constraints (the editor's rules list): formulas, linked
 * values (step ↔ count, a depth that follows params), handles that set
 * params, and shapes, copies or shifts computed from a param. A basic
 * object may still use the standard modifiers (repeats, mirror, round
 * corners), show or hide parts with a toggle, and follow its own size (pinned
 * points, a door's swing): a radiator, a column or a window stays normal.
 * The organisation's own objects carry their `smart` flag instead.
 */
export const isSmartSpec = (spec: SmartSpecFields): boolean => {
  if ((spec.formulas?.length ?? 0) > 0 || (spec.stepSyncs?.length ?? 0) > 0 || spec.depthLink) return true;
  const { start, end, depth } = spec.handles ?? {};
  if ((start && isFormulaHandlePoint(start)) || (end && isFormulaHandlePoint(end)) || depth?.some((handle) => handle.assign || handle.at)) return true;
  const params = spec.params.map((param) => param.key);
  if (params.length === 0) return false;
  const groupRule = spec.groups.some(
    (group) => (group.repeat?.kind === 'index' && bindingReadsName(group.repeat.count, params)) || Object.values(group.offset ?? {}).some((expression) => readsName(expression, params)),
  );
  return groupRule || spec.shapes.some((shape) => Object.values(shape.expr ?? {}).some((expression) => readsName(expression, params)));
};
